// User profile and preferences APIs

import axiosInstance from '../config/axios.config';
import ENDPOINTS from '../config/endpoints';
import tokenManager from '../utils/tokenManager';

class UserService {
  // Get user profile by UUID
  async getProfile(uuid) {
    try {
      const targetUuid = uuid || tokenManager.getUserData()?.uuid || tokenManager.getUserData()?.id;
      if (!targetUuid) {
        throw new Error('User identifier not found');
      }
      
      const response = await axiosInstance.get(ENDPOINTS.USER.PROFILE(targetUuid));
      const { status, message, data } = response.data;
      
      if (status) {
        return {
          success: true,
          data: data,
        };
      }
      throw new Error(message || 'Failed to fetch profile');
    } catch (error) {
      throw error;
    }
  }

  // Update user profile
  async updateProfile(uuid, profileData) {
    try {
      // Ensure mobile is string
      const dataToSend = {
        ...profileData,
        mobile: String(profileData.mobile),
      };
      
      const response = await axiosInstance.put(
        ENDPOINTS.USER.UPDATE_PROFILE(uuid),
        dataToSend
      );
      
      const { status, message, data } = response.data;
      
      if (status) {
        // Update user data in localStorage with new data from backend
        tokenManager.setUserData(data);
        
        // Dispatch event to notify app of user data update
        window.dispatchEvent(new Event('userDataUpdated'));
        
        return {
          success: true,
          message,
          data,
        };
      }
      
      throw new Error(message || 'Profile update failed');
    } catch (error) {
      console.error('[UserService] Update profile error:', error);
      throw error;
    }
  }

  // List all addresses for current user
  async listAddresses() {
    try {
      const response = await axiosInstance.get(ENDPOINTS.USER.ADDRESS_LIST);
      const { status, success, message, data } = response.data;
      
      if (status || success) {
        return {
          success: true,
          data: data || [],
        };
      }
      throw new Error(message || 'Failed to fetch addresses');
    } catch (error) {
      console.error('[UserService] List addresses error:', error);
      throw error;
    }
  }

  // Create a new user address
  async createAddress(addressData) {
    try {
      const response = await axiosInstance.post(
        ENDPOINTS.USER.ADDRESS_CREATE,
        addressData
      );
      
      const { status, success, message, data } = response.data;
      
      if (status || success) {
        return {
          success: true,
          message,
          data,
        };
      }
      
      throw new Error(message || 'Failed to create address');
    } catch (error) {
      console.error('[UserService] Create address error:', error);
      throw error;
    }
  }

  // Update an existing address
  async updateAddress(uuid, addressData) {
    try {
      const response = await axiosInstance.put(
        ENDPOINTS.USER.ADDRESS_UPDATE(uuid),
        addressData
      );
      
      const { status, success, message, data } = response.data;
      
      if (status || success) {
        return {
          success: true,
          message,
          data,
        };
      }
      throw new Error(message || 'Failed to update address');
    } catch (error) {
      console.error('[UserService] Update address error:', error);
      throw error;
    }
  }

  // Delete an address
  async deleteAddress(uuid) {
    try {
      const response = await axiosInstance.delete(
        ENDPOINTS.USER.ADDRESS_DELETE(uuid)
      );
      
      const { status, success, message } = response.data;
      
      if (status || success) {
        return {
          success: true,
          message,
        };
      }
      throw new Error(message || 'Failed to delete address');
    } catch (error) {
      console.error('[UserService] Delete address error:', error);
      throw error;
    }
  }

  // Validate mobile number (10 digits)
  validateMobile(mobile) {
    if (!mobile) return false;
    const mobileStr = String(mobile).replace(/\D/g, ''); // Remove non-digits
    return mobileStr.length === 10;
  }

  // Format mobile number for display (999-999-9999)
  formatMobile(mobile) {
    const mobileStr = String(mobile).replace(/\D/g, '');
    if (mobileStr.length === 10) {
      return `${mobileStr.slice(0, 3)}-${mobileStr.slice(3, 6)}-${mobileStr.slice(6)}`;
    }
    return mobile;
  }

  // Validate profile data with user-friendly messages
  validateProfileData(data) {
    const errors = {};

    // First name validation
    if (!data.first_name || data.first_name.trim() === '') {
      errors.first_name = 'Please enter your first name';
    } else if (data.first_name.trim().length < 2) {
      errors.first_name = 'First name should be at least 2 characters';
    } else if (data.first_name.length > 50) {
      errors.first_name = 'First name is too long (maximum 50 characters)';
    }

    // Last name validation
    if (!data.last_name || data.last_name.trim() === '') {
      errors.last_name = 'Please enter your last name';
    } else if (data.last_name.trim().length < 2) {
      errors.last_name = 'Last name should be at least 2 characters';
    } else if (data.last_name.length > 50) {
      errors.last_name = 'Last name is too long (maximum 50 characters)';
    }

    // Mobile validation
    if (!data.mobile || data.mobile.trim() === '') {
      errors.mobile = 'Please enter your mobile number';
    } else if (!this.validateMobile(data.mobile)) {
      const mobileStr = String(data.mobile).replace(/\D/g, '');
      if (mobileStr.length < 10) {
        errors.mobile = `Please enter a complete 10-digit mobile number (you entered ${mobileStr.length} digits)`;
      } else {
        errors.mobile = 'Please enter a valid 10-digit mobile number';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }
}

// Export singleton instance
const userService = new UserService();
export default userService;
